import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  ShoppingListSummary,
  ShoppingListItem,
  ShoppingListWithItems,
  CreateShoppingListData,
  UpdateShoppingListData,
  CreateShoppingListItemData,
  UpdateShoppingListItemData,
  ReorderShoppingListItemsData,
  BatchShoppingListItemsData,
  BatchResult,
  PaginatedShoppingListsResponse,
} from '@pantry/shared';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all shopping lists for a household.
 *
 * @param householdId - The household ID
 * @param params - Optional query params (e.g. filtering)
 */
async function getShoppingLists(
  householdId: string,
  params?: Record<string, unknown>
): Promise<PaginatedShoppingListsResponse> {
  const searchParams = new URLSearchParams();
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    }
  }
  const query = searchParams.toString();
  const url = `/households/${householdId}/shopping-lists${query ? `?${query}` : ''}`;
  return apiFetch<PaginatedShoppingListsResponse>(url);
}

/**
 * Fetch a single shopping list with its items.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 */
async function getShoppingList(
  householdId: string,
  listId: string
): Promise<ShoppingListWithItems> {
  return apiFetch<ShoppingListWithItems>(`/households/${householdId}/shopping-lists/${listId}`);
}

/**
 * Create a new shopping list.
 *
 * @param householdId - The household ID
 * @param data - Creation payload
 */
async function createShoppingList(
  householdId: string,
  data: CreateShoppingListData
): Promise<ShoppingListSummary> {
  return apiFetch<ShoppingListSummary>(`/households/${householdId}/shopping-lists`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing shopping list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param data - Update payload
 */
async function updateShoppingList(
  householdId: string,
  listId: string,
  data: UpdateShoppingListData
): Promise<ShoppingListWithItems> {
  return apiFetch<ShoppingListWithItems>(`/households/${householdId}/shopping-lists/${listId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a shopping list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 */
async function deleteShoppingList(householdId: string, listId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/shopping-lists/${listId}`, {
    method: 'DELETE',
  });
}

/**
 * Add an item to a shopping list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param data - Item creation payload
 */
async function addShoppingListItem(
  householdId: string,
  listId: string,
  data: CreateShoppingListItemData
): Promise<ShoppingListItem> {
  return apiFetch<ShoppingListItem>(`/households/${householdId}/shopping-lists/${listId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an item within a shopping list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param itemId - The item ID
 * @param data - Update payload
 */
async function updateShoppingListItem(
  householdId: string,
  listId: string,
  itemId: string,
  data: UpdateShoppingListItemData
): Promise<ShoppingListItem> {
  return apiFetch<ShoppingListItem>(
    `/households/${householdId}/shopping-lists/${listId}/items/${itemId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Delete an item from a shopping list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param itemId - The item ID
 */
async function deleteShoppingListItem(
  householdId: string,
  listId: string,
  itemId: string
): Promise<void> {
  await apiFetch(`/households/${householdId}/shopping-lists/${listId}/items/${itemId}`, {
    method: 'DELETE',
  });
}

/**
 * Reorder items within a shopping list.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param data - Ordered array of item IDs defining the new sort order
 */
async function reorderShoppingListItems(
  householdId: string,
  listId: string,
  data: ReorderShoppingListItemsData
): Promise<void> {
  await apiFetch(`/households/${householdId}/shopping-lists/${listId}/items/reorder`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Run a batch action on a shopping list's items.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 * @param data - Batch action payload
 */
async function batchShoppingListItems(
  householdId: string,
  listId: string,
  data: BatchShoppingListItemsData
): Promise<BatchResult> {
  return apiFetch<BatchResult>(`/households/${householdId}/shopping-lists/${listId}/items/batch`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

const DEFAULT_PAGE_SIZE = 20;

/**
 * Hook to fetch paginated shopping lists for a household.
 * Uses infinite query for "load more" / infinite scroll.
 *
 * Flattens pages into a single `items` array for convenience.
 *
 * @param householdId - The household ID
 * @param params - Optional query params (isActive, etc.)
 */
export function useShoppingLists(householdId: string, params?: Record<string, unknown>) {
  const limit = (params?.limit as number) ?? DEFAULT_PAGE_SIZE;

  const query = useInfiniteQuery({
    queryKey: queryKeys.shoppingLists.list(householdId, params),
    queryFn: ({ pageParam = 0 }) =>
      getShoppingLists(householdId, { ...params, limit, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = (lastPageParam as number) + limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Flatten pages for consumer convenience
  const items = query.data?.pages.flatMap((p) => p.items);

  // Destructure to replace `data` with flattened items while preserving other query props
  const { data: _rawPages, ...rest } = query;

  return {
    ...rest,
    /** Flattened list of all loaded shopping list summaries */
    data: items,
  };
}

/**
 * Hook to fetch a single shopping list with its items.
 *
 * @param householdId - The household ID
 * @param listId - The shopping list ID
 */
export function useShoppingList(householdId: string, listId: string) {
  return useQuery({
    queryKey: queryKeys.shoppingLists.detail(householdId, listId),
    queryFn: () => getShoppingList(householdId, listId),
    enabled: !!householdId && !!listId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new shopping list.
 * Invalidates the household's shopping list cache on success.
 */
export function useCreateShoppingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, data }: { householdId: string; data: CreateShoppingListData }) =>
      createShoppingList(householdId, data),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to update an existing shopping list.
 * Updates the detail cache entry and invalidates the list cache on success.
 */
export function useUpdateShoppingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      data,
    }: {
      householdId: string;
      listId: string;
      data: UpdateShoppingListData;
    }) => updateShoppingList(householdId, listId, data),
    onSuccess: (updated, { householdId, listId }) => {
      const key = queryKeys.shoppingLists.detail(householdId, listId);
      queryClient.setQueryData<ShoppingListWithItems>(key, (old) =>
        old ? { ...old, ...updated, items: updated.items ?? old.items } : old
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to delete a shopping list.
 * Invalidates the household's shopping list cache on success.
 */
export function useDeleteShoppingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, listId }: { householdId: string; listId: string }) =>
      deleteShoppingList(householdId, listId),
    onSuccess: (_, { householdId, listId }) => {
      queryClient.removeQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to add an item to a shopping list.
 * Invalidates both the list detail and household list cache on success.
 */
export function useAddShoppingListItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      data,
    }: {
      householdId: string;
      listId: string;
      data: CreateShoppingListItemData;
    }) => addShoppingListItem(householdId, listId, data),
    onMutate: async ({ householdId, listId, data }) => {
      const queryKey = queryKeys.shoppingLists.detail(householdId, listId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ShoppingListWithItems>(queryKey);

      if (previous) {
        const tempItem: ShoppingListItem = {
          id: crypto.randomUUID(),
          shoppingListId: listId,
          productId: data.productId ?? null,
          customName: data.customName ?? null,
          displayName: data.customName ?? 'New Item',
          quantity: data.quantity ?? 1,
          unit: data.unit ?? 'item',
          isChecked: false,
          checkedAt: null,
          checkedBy: null,
          notes: data.notes ?? null,
          sortOrder: previous.items.length,
          productGroupId: null,
          productGroup: null,
          checkedByUser: null,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        queryClient.setQueryData<ShoppingListWithItems>(queryKey, {
          ...previous,
          itemCount: previous.itemCount + 1,
          items: [...previous.items, tempItem],
        });
      }

      return { previous, householdId, listId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.shoppingLists.detail(context.householdId, context.listId),
          context.previous
        );
      }
    },
    onSettled: (_, _err, { householdId, listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to update a shopping list item.
 * Applies an optimistic update for check/uncheck interactions to reduce perceived latency.
 * Rolls back on error and always revalidates on settle.
 */
export function useUpdateShoppingListItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      itemId,
      data,
    }: {
      householdId: string;
      listId: string;
      itemId: string;
      data: UpdateShoppingListItemData;
    }) => updateShoppingListItem(householdId, listId, itemId, data),
    onMutate: async ({ householdId, listId, itemId, data }) => {
      const queryKey = queryKeys.shoppingLists.detail(householdId, listId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ShoppingListWithItems>(queryKey);

      if (previous) {
        queryClient.setQueryData<ShoppingListWithItems>(queryKey, (old) => {
          if (!old) return old;
          const existingItem = old.items.find((i) => i.id === itemId);
          const wasChecked = existingItem?.isChecked ?? false;
          const willBeChecked = data.isChecked ?? wasChecked;
          const checkedCountDelta = willBeChecked !== wasChecked ? (willBeChecked ? 1 : -1) : 0;
          return {
            ...old,
            checkedCount: Math.max(0, old.checkedCount + checkedCountDelta),
            items: old.items.map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    ...data,
                    ...(data.isChecked !== undefined && {
                      checkedAt: data.isChecked ? new Date() : null,
                      checkedBy: data.isChecked ? null : null, // Will be corrected on server response
                    }),
                  }
                : item
            ),
          };
        });
      }

      return { previous, householdId, listId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.shoppingLists.detail(context.householdId, context.listId),
          context.previous
        );
      }
    },
    onSettled: (_data, _error, { householdId, listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to delete an item from a shopping list.
 * Invalidates both the list detail and household list cache on success.
 */
export function useDeleteShoppingListItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      itemId,
    }: {
      householdId: string;
      listId: string;
      itemId: string;
    }) => deleteShoppingListItem(householdId, listId, itemId),
    onMutate: async ({ householdId, listId, itemId }) => {
      const queryKey = queryKeys.shoppingLists.detail(householdId, listId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ShoppingListWithItems>(queryKey);

      if (previous) {
        const deletedItem = previous.items.find((item) => item.id === itemId);
        queryClient.setQueryData<ShoppingListWithItems>(queryKey, {
          ...previous,
          itemCount: Math.max(0, previous.itemCount - 1),
          checkedCount: deletedItem?.isChecked
            ? Math.max(0, previous.checkedCount - 1)
            : previous.checkedCount,
          items: previous.items.filter((item) => item.id !== itemId),
        });
      }

      return { previous, householdId, listId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.shoppingLists.detail(context.householdId, context.listId),
          context.previous
        );
      }
    },
    onSettled: (_, _err, { householdId, listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to reorder items within a shopping list.
 * Applies an optimistic reorder in the detail cache, then revalidates on settle.
 */
export function useReorderShoppingListItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      data,
    }: {
      householdId: string;
      listId: string;
      data: ReorderShoppingListItemsData;
    }) => reorderShoppingListItems(householdId, listId, data),
    onMutate: async ({ householdId, listId, data }) => {
      const queryKey = queryKeys.shoppingLists.detail(householdId, listId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ShoppingListWithItems>(queryKey);

      if (previous) {
        queryClient.setQueryData<ShoppingListWithItems>(queryKey, (old) => {
          if (!old) return old;
          const itemMap = new Map(old.items.map((item) => [item.id, item]));
          const reordered = data.itemIds
            .map((id) => itemMap.get(id))
            .filter((item): item is ShoppingListWithItems['items'][number] => item !== undefined);
          // Append any items not present in the new order (defensive)
          const reorderedIds = new Set(data.itemIds);
          const remaining = old.items.filter((item) => !reorderedIds.has(item.id));
          return { ...old, items: [...reordered, ...remaining] };
        });
      }

      return { previous, householdId, listId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.shoppingLists.detail(context.householdId, context.listId),
          context.previous
        );
      }
    },
    onSettled: (_data, _error, { householdId, listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to run a batch action on a shopping list's items.
 * Invalidates both the list detail and household list cache on success.
 */
export function useBatchShoppingListItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      listId,
      data,
    }: {
      householdId: string;
      listId: string;
      data: BatchShoppingListItemsData;
    }) => batchShoppingListItems(householdId, listId, data),
    onMutate: async ({ householdId, listId, data }) => {
      const queryKey = queryKeys.shoppingLists.detail(householdId, listId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ShoppingListWithItems>(queryKey);

      if (previous) {
        let updatedItems = previous.items;
        let itemCount = previous.itemCount;
        let checkedCount = previous.checkedCount;

        switch (data.action) {
          case 'uncheck-all':
            updatedItems = previous.items.map((item) => ({
              ...item,
              isChecked: false,
              checkedAt: null,
              checkedBy: null,
            }));
            checkedCount = 0;
            break;
          case 'delete-checked':
            updatedItems = previous.items.filter((item) => !item.isChecked);
            itemCount = updatedItems.length;
            checkedCount = 0;
            break;
          case 'clear':
            updatedItems = [];
            itemCount = 0;
            checkedCount = 0;
            break;
        }

        queryClient.setQueryData<ShoppingListWithItems>(queryKey, {
          ...previous,
          items: updatedItems,
          itemCount,
          checkedCount,
        });
      }

      return { previous, householdId, listId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.shoppingLists.detail(context.householdId, context.listId),
          context.previous
        );
      }
    },
    onSettled: (_, _err, { householdId, listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.detail(householdId, listId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shoppingLists.byHousehold(householdId),
      });
    },
  });
}
